import { EmailMessage } from '../email/EmailTypes';

// The statuses of a ticket, a ticket is open until it is resolved
export type TicketStatus = 'new' | 'open' | 'parked' | 'awaiting' | 'resolved';

// The ordered list of ticket statuses as shown in the status bar
export const TICKET_STATUSES: TicketStatus[] = ['new', 'open', 'parked', 'awaiting', 'resolved'];

// The statuses a user can set from the composer
export const SELECTABLE_TICKET_STATUSES: TicketStatus[] = ['open', 'awaiting', 'parked', 'resolved'];

// The views of the inbox column, the inbox holds the new tickets and the unanswered replies
export type TicketInboxView = 'inbox' | 'open' | 'parked' | 'awaiting' | 'resolved';

// The counts of the inbox views returned as list meta data
export interface TicketViewCounts {
    inbox: number;
    open: number;
    parked: number;
    awaiting: number;
}

// The direction of a ticket message
export type TicketMessageDirection = 'inbound' | 'outbound' | 'internal';

// The type of a ticket message
export type TicketMessageType = 'message' | 'note' | 'system' | 'link';

// A proposal, job or deal created from a ticket, with its current status
export interface TicketLinkedItem {
    type: 'proposal' | 'job' | 'deal';
    id: number;
    deleted: boolean;
    number?: string | null;
    status?: string;
    status_name?: string;
    status_condition?: string;
    title?: string;
    stage_name?: string;
    resolution?: string;
}

// The linked items of a ticket as returned by the backend
export interface TicketLinkedItems {
    related_proposals: TicketLinkedItem[];
    related_jobs: TicketLinkedItem[];
    related_deals: TicketLinkedItem[];
    tracked_hours: number;
}

// The contact details of the reporter shown in the reporter card
export interface TicketReporterDetails {
    id: number;
    name: string;
    entity_type: 'person' | 'organization' | null;
    email: string | null;
    phone_number: string | null;
    organization_id: number | null;
    organization_name: string | null;
    address: string | null;
}

// A ticket as returned by the backend
export interface Ticket {
    id: number;
    number: string | null;
    subject: string | null;
    status: TicketStatus;
    category: number | null;
    category_name: string | null;
    reporter: number | null;
    reporter_name: string | null;
    reporter_email: string | null;
    reporter_email_on_own_domain?: boolean;
    reporter_display_name: string | null;
    reporter_details?: TicketReporterDetails | null;
    reporter_contact?: Record<string, any> | null;
    reporter_organization?: Record<string, any> | null;
    assignee: number | null;
    assignee_name: string | null;
    division: number | null;
    last_message_at: string | null;
    last_message_direction: TicketMessageDirection | null;
    awaiting_response: boolean;
    new_reply: boolean;
    resolved_at: string | null;
    created: string;
    deleted: boolean;
    inbound_address?: string;
}

// A message, note or system line of a ticket conversation
export interface TicketMessage {
    id: number;
    ticket: number;
    type: TicketMessageType;
    direction: TicketMessageDirection;
    subject: string | null;
    body: string | null;
    body_text: string | null;
    from_name: string | null;
    from_email: string | null;
    author: number | null;
    author_name: string | null;
    author_profile_picture: string | null;
    attachments: { id: number; file: string; filename: string }[];
    recipients: string[];
    delivery_status: 'queued' | 'sent' | 'failed' | null;
    is_acknowledgement: boolean;
    email_message_data: EmailMessage | null;
    linked_item: TicketLinkedItem | null;
    is_read: boolean;
    created: string;
}

// A ticket category configured in the settings
export interface TicketCategory {
    id: number;
    name: string;
    ordering: number;
    is_active: boolean;
}

